// @flow
import { Map, Record } from "immutable"

import { PLACE_VALUE_MODES, PLACE_TYPES } from "../consts/place"
import type { PlaceValueMode } from "../consts/place"
import Place from "./Place"
import Radius from "common/records/Radius"

type Value = string | Place | Radius
export type Geo = { lat: number, lng: number }

export type Input =
  | string
  | {
      mode?: PlaceValueMode,
      value?: Value,
      isDefault?: boolean,
    }

export type Plain = {
  mode?: PlaceValueMode,
  value?: Object | string, // eslint-disable-line flowtype/no-weak-types
  isDefault?: boolean,
}

const SearchPlaceRecord = Record({
  mode: PLACE_VALUE_MODES.TEXT,
  value: "",
  isDefault: false,
})

class SearchPlace extends SearchPlaceRecord {
  constructor(raw: Input = {}) {
    let input = raw // TODO: Check
    if (typeof input === "string") {
      const value = input
      input = {
        value,
      }
    }

    super(input)
  }

  getType() {
    switch (this.mode) {
      case PLACE_VALUE_MODES.ANYWHERE:
        return PLACE_VALUE_MODES.ANYWHERE
      case PLACE_VALUE_MODES.RADIUS:
        return PLACE_VALUE_MODES.RADIUS
      case PLACE_VALUE_MODES.PLACE:
        return this.value.getType()
      default:
        return "place"
    }
  }

  getPlaces(placesFromStore: Map<string, Place>): Place[] {
    switch (this.mode) {
      case PLACE_VALUE_MODES.PLACE:
        return [this.value]
      case PLACE_VALUE_MODES.RADIUS:
        return this.value.getPlacesInRadius(placesFromStore).toArray()
      default:
        return []
    }
  }

  getId(): ?string {
    switch (this.mode) {
      case PLACE_VALUE_MODES.PLACE:
        return this.value.id

      case PLACE_VALUE_MODES.RADIUS:
        return this.value.getId()

      case PLACE_VALUE_MODES.ID:
      case PLACE_VALUE_MODES.TEXT:
        return this.value

      case PLACE_VALUE_MODES.ANYWHERE:
        return this.mode

      case PLACE_VALUE_MODES.UNSELECTED:
        return "-"

      default:
        return ""
    }
  }

  getSearchId(): ?string {
    switch (this.mode) {
      case PLACE_VALUE_MODES.PLACE:
        return this.value.id

      case PLACE_VALUE_MODES.RADIUS:
        return this.value.getSearchId()

      case PLACE_VALUE_MODES.ID:
      case PLACE_VALUE_MODES.TEXT:
        return this.value

      case PLACE_VALUE_MODES.ANYWHERE:
        return this.mode

      case PLACE_VALUE_MODES.UNSELECTED:
        return "-"

      default:
        return null
    }
  }

  getPlaceValue(): ?Place {
    switch (this.mode) {
      case PLACE_VALUE_MODES.RADIUS:
        return this.value.closeCity
      case PLACE_VALUE_MODES.PLACE:
        return this.value
      default:
        return null
    }
  }

  getStrictPlaceValue(): ?Place {
    return this.mode === PLACE_VALUE_MODES.PLACE ? this.value : null
  }

  // is a city or an airport (or station ... which is basically an airport ... just without runway and planes and useless security checks)
  isCityOrAirport(): boolean {
    const { mode, value } = this
    return (
      mode === PLACE_VALUE_MODES.PLACE &&
      (value.type === PLACE_TYPES.TRAIN_STATION ||
        value.type === PLACE_TYPES.AIRPORT ||
        value.type === PLACE_TYPES.BUS_STATION ||
        value.type === PLACE_TYPES.CITY ||
        value.type === PLACE_TYPES.SPECIAL)
    )
  }

  isSpecial(): boolean {
    return this.mode === PLACE_VALUE_MODES.PLACE && this.value.type === PLACE_TYPES.SPECIAL
  }

  isSimple(): boolean {
    const { mode } = this
    return mode === PLACE_VALUE_MODES.ID || this.isCityOrAirport()
  }

  // is larger than one city
  isLarge(): boolean {
    const { mode } = this
    return (
      mode === PLACE_VALUE_MODES.RADIUS ||
      mode === PLACE_VALUE_MODES.ANYWHERE ||
      (mode === PLACE_VALUE_MODES.PLACE && !this.isCityOrAirport())
    )
  }

  isCountry(): boolean {
    return this.value.type === PLACE_TYPES.COUNTRY
  }

  isCountryOrBigger(): boolean {
    return (
      this.mode === PLACE_VALUE_MODES.ANYWHERE ||
      (this.mode === PLACE_VALUE_MODES.PLACE && !this.isCityOrAirport())
    )
  }

  isAnywhere(): boolean {
    return this.mode === PLACE_VALUE_MODES.ANYWHERE || this.value.id === "anywhere"
  }

  static fromPlainJS(plain: Plain): SearchPlace {
    const input = {}
    if (plain.mode === PLACE_VALUE_MODES.RADIUS) {
      input.value = Radius.fromPlainJS(plain.value)
    }

    if (plain.mode === PLACE_VALUE_MODES.PLACE) {
      input.value = Place.fromPlainJS(plain.value)
    }

    return new SearchPlace({ ...plain, ...input })
  }

  static anywhere(): SearchPlace {
    return new SearchPlace({ mode: PLACE_VALUE_MODES.ANYWHERE })
  }

  getGeo(): ?Geo {
    const { mode, value } = this
    if (
      (mode === PLACE_VALUE_MODES.PLACE && value.checkLatLng()) ||
      mode === PLACE_VALUE_MODES.RADIUS
    ) {
      return {
        lat: value.lat,
        lng: value.lng,
      }
    }
    return null
  }

  hasParent(parent: SearchPlace): boolean {
    if (parent.getId() === "anywhere") return true
    if (this.mode !== PLACE_VALUE_MODES.PLACE) return false

    return parent.mode === PLACE_VALUE_MODES.RADIUS
      ? parent.value.isPlaceInRadius(this.value)
      : this.value.isIncludedDeepIn([parent.getSearchId()])
  }

  equals(place: SearchPlace): boolean {
    return this.getSearchId() === place.getSearchId()
  }
}

export default SearchPlace
